import { useSQLiteContext } from 'expo-sqlite';
import { useEffect } from 'react';
import { Platform } from 'react-native';

import { computeAchievementStats, computeAchievements } from './achievements';
import { useDataRevision } from './data-revision';
import { postMessage } from './messages';
import {
  NOTIFICATIONS_AVAILABLE,
  ensureAndroidNotificationChannel,
  getNotificationPermissionStatus,
  isInQuietHours,
  showImmediateWebNotification,
} from './notifications';
import { getActiveProperty } from './tasks';

import * as Notifications from 'expo-notifications';

/**
 * Watches the user's achievement state and posts a message every time a
 * badge transitions from unearned → earned. Sources truth from the same
 * computeAchievements derivation the screen uses, so there's no risk of
 * UI and notification disagreeing.
 *
 * The "seen" set is module-level (not persisted) — on a fresh app boot
 * we treat every already-earned badge as "already seen" and don't toast.
 * Otherwise reloading the page would spam the inbox with every historical
 * achievement. The first revision tick after mount records the baseline;
 * everything from there on triggers a message.
 */

const seenEarned = new Set<string>();
let initialized = false;

export function useAchievementWatcher(): void {
  // No-op: the Achievements UI was removed (more noise than value), and
  // posting "you earned X" inbox messages without a destination to tap
  // into reads as broken. We keep the function in place so the call
  // site in AppGlobals doesn't have to change, and the achievements
  // tables / helper functions stay available for any future re-enable.
  return;
  // eslint-disable-next-line no-unreachable
  const db = useSQLiteContext();
  // eslint-disable-next-line no-unreachable
  const revision = useDataRevision();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const prop = await getActiveProperty(db);
      if (!prop) return;
      const stats = await computeAchievementStats(db, prop.id);
      const list = computeAchievements(stats);
      if (cancelled) return;

      if (!initialized) {
        // Baseline run — record everything already earned without
        // toasting. Subsequent ticks compare against this set.
        for (const a of list) {
          if (a.earned) seenEarned.add(a.key);
        }
        initialized = true;
        return;
      }

      // Cache the OS permission + notify_achievements toggle once per
      // tick instead of per-achievement — most ticks find zero new
      // unlocks and we'd rather not hit the OS or DB needlessly.
      const notifyAchievementsOn = (prop.notify_achievements ?? 1) === 1;
      const masterOn = (prop.notifications_enabled ?? 1) === 1;
      const soundOn = (prop.notify_sound ?? 1) === 1;
      const inQuiet = isInQuietHours(prop.quiet_hours_start, prop.quiet_hours_end);
      let permission: string | null = null;

      for (const a of list) {
        if (a.earned && !seenEarned.has(a.key)) {
          seenEarned.add(a.key);
          // 1. Always post to the inbox — the inbox is the durable record
          //    even when push is off, and useUnreadCount drives the bell badge.
          void postMessage(db, {
            tone: 'achievement',
            title: `Achievement unlocked: ${a.title}`,
            body: a.body,
          }).catch((err) => {
            console.warn('postMessage for achievement failed', err);
          });

          // 2. Optionally fire an OS push notification on top. Requires
          //    master switch + per-category opt-in + OS permission, AND
          //    we suppress the OS fire during quiet hours so achievements
          //    can't wake the user up at 2am. The inbox post above
          //    happened regardless, so nothing is lost.
          if (masterOn && notifyAchievementsOn && !inQuiet) {
            if (permission === null) {
              permission = await getNotificationPermissionStatus().catch(() => 'denied');
            }
            if (permission === 'granted') {
              if (Platform.OS === 'web') {
                void showImmediateWebNotification(
                  `Achievement unlocked`,
                  `${a.title} — ${a.body}`,
                  '/achievements'
                ).catch(() => {});
              } else if (NOTIFICATIONS_AVAILABLE) {
                await ensureAndroidNotificationChannel();
                void Notifications.scheduleNotificationAsync({
                  content: {
                    title: 'Achievement unlocked',
                    body: `${a.title} — ${a.body}`,
                    data: { kind: 'achievement', key: a.key },
                    sound: soundOn ? 'default' : false,
                  },
                  trigger: null, // immediate
                }).catch(() => {});
              }
            }
          }
        }
      }
    })();
    return () => { cancelled = true; };
  }, [db, revision]);
}
